<template>
    <v-container
        class="content-block-v2"
        :class="[
            blockClass,
            themeClasses,
            {
                'pa-0': !padBlock,
                'h-100': fullBleed
            }
        ]"
        :fluid="fullBleed || modelValue.values.widthType === 'fluid'"
    >
        <component
            :is="currentBlock"
            :model-value="modelValue.values"
            :is-mobile="isMobile"
            :breakpoint="breakpoint"
            :breakpoint-name="breakpointName"
        ></component>
    </v-container>
</template>

<script lang="ts" setup>
    import type {
        ContentBlockRendered
    } from '~/types';
    import { useDisplay, useTheme } from 'vuetify';
    import { kebabCase as lo_kebabCase } from 'es-toolkit';
    import { isUndefined as lo_isUndefined } from 'es-toolkit';


    const props = defineProps<{
        modelValue: ContentBlockRendered;
        fullBleed?: boolean;
    }>();

    const {
        themeClasses
    } = useTheme();

    const {
        mobile: isMobile,
        name: breakpointName
    } = useDisplay();

    const blocks = {
        BlockXSplitFeature: resolveComponent('LazyContentBlocksV2BlockXSplitFeature'),
        BlockYSplitFeature: resolveComponent('LazyContentBlocksV2BlockYSplitFeature'),
        BlockZSplitFeature: resolveComponent('LazyContentBlocksV2BlockZSplitFeature'),
        BlockXSplitHeroCarouselItem: resolveComponent('LazyContentBlocksV2BlockXSplitHeroCarouselItem'),
        BlockYSplitHeroCarouselItem: resolveComponent('LazyContentBlocksV2BlockYSplitHeroCarouselItem'),
        BlockZSplitHeroCarouselItem: resolveComponent('LazyContentBlocksV2BlockZSplitHeroCarouselItem'),
        BlockShowcaseTile: resolveComponent('LazyContentBlocksV2BlockShowcaseTile')
    };

    const blockClass = computed<string>(() => {
        const templateRef = props.modelValue.template?.ref ?? 'unknown';
        return `content-block-${lo_kebabCase(templateRef)}`;
    });

    const currentBlock = computed<Nullable<typeof blocks[keyof typeof blocks]>>(() => {
        const templateRef = props.modelValue.template?.ref;

        if (!templateRef) return null;

        const block = blocks[`Block${templateRef}` as keyof typeof blocks];

        if (!block) {
            console.warn(`BlockRenderer: No component found for block name "Block${templateRef}".`);
            return null;
        }

        return block;
    });

    const breakpoint = computed<string>(() => (isMobile.value ? 'mobile' : 'desktop'));

    const padBlock = computed<boolean>(() => {
        // `fullBleed` overrides the CMS setting: a full-bleed block is never padded.
        if (props.fullBleed) return false;

        // Otherwise blocks are padded unless the CMS value explicitly turns it off.
        const value = props.modelValue.values.padBlock;
        return lo_isUndefined(value) || Boolean(value);
    });
</script>

<style lang="scss"></style>
